import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { netBadge } from "@/components/RecordManager";
import { subjectsForTrack } from "@/lib/constants";
import { Plus, Trash2, Loader2, ListChecks, X } from "lucide-react";

const HEAD_STYLE = [
  "bg-blue-600", "bg-emerald-600", "bg-amber-500", "bg-purple-600", "bg-rose-600",
  "bg-teal-600", "bg-orange-500", "bg-pink-600", "bg-indigo-600", "bg-cyan-600",
];

const EMPTY = { date: "", subject: "", resource_name: "", unit_no: "", unit_name: "", total: "", correct: "", wrong: "", blank: "" };

const dayOf = (d) => {
  if (!d) return "-";
  const dt = new Date(`${d}T12:00:00`);
  return isNaN(dt) ? "-" : dt.toLocaleDateString("tr-TR", { weekday: "long" });
};

export default function DailyQuestions({ studentId, readOnly, track }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const subjects = subjectsForTrack(track);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/daily-questions/${studentId}`);
      setItems(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (studentId) load();
  }, [load, studentId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.subject || form.total === "" || form.correct === "" || form.wrong === "") {
      return toast.error("Tarih, ders, toplam, doğru ve yanlış zorunlu");
    }
    setSaving(true);
    try {
      await api.post(`/daily-questions/${studentId}`, {
        ...form,
        total: Number(form.total), correct: Number(form.correct),
        wrong: Number(form.wrong), blank: Number(form.blank || 0),
      });
      toast.success("Kayıt eklendi");
      setForm(EMPTY);
      load();
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/daily-questions/${studentId}/${id}`);
      setItems((p) => p.filter((x) => x.id !== id));
      toast.success("Silindi");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const [resOpen, setResOpen] = useState(null);
  const [resSaving, setResSaving] = useState(false);

  const openResultEntry = () => {
    const rows = items
      .filter((r) => !Number(r.correct) && !Number(r.wrong))
      .map((r) => ({ ...r, t: "", dc: "", yc: "", bl: "" }));
    if (!rows.length) return toast.info("Sonuç bekleyen kayıt yok");
    setResOpen(rows);
  };

  const saveResults = async () => {
    const updates = resOpen
      .filter((r) => r.dc !== "" || r.yc !== "" || r.bl !== "")
      .map((r) => ({
        id: r.id,
        total: Number(r.t || 0) || Number(r.dc || 0) + Number(r.yc || 0) + Number(r.bl || 0),
        correct: Number(r.dc || 0),
        wrong: Number(r.yc || 0),
        blank: Number(r.bl || 0),
      }));
    if (!updates.length) return toast.error("En az bir satır doldurun");
    setResSaving(true);
    try {
      const { data } = await api.put(`/records-bulk/daily-questions/${studentId}`, { updates });
      toast.success(`${data.updated} kayıt sonucu kaydedildi — net otomatik hesaplandı`);
      setResOpen(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setResSaving(false);
    }
  };

  const groups = [...new Set(items.map((i) => i.subject || "Diğer"))];
  const grand = items.reduce((a, r) => ({
    t: a.t + Number(r.total || 0), c: a.c + Number(r.correct || 0), w: a.w + Number(r.wrong || 0), b: a.b + Number(r.blank || 0),
  }), { t: 0, c: 0, w: 0, b: 0 });

  return (
    <div className="space-y-5" data-testid="daily-questions-panel">
      <div>
        <h3 className="font-display text-xl font-bold text-slate-900">Günlük Soru Takibi</h3>
        <p className="text-sm text-slate-500 mt-0.5">
          Her ders kendi tablosunda; gün ve tarih, toplam/doğru/yanlış/boş ve net ayrı ayrı gösterilir. 'Kaynak' satırları Kaynak Takibi'nden otomatik gelir.
        </p>
      </div>

      {!readOnly && (
        <form onSubmit={submit} className="card-ta p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <div>
              <label className="ta-label">Tarih</label>
              <input type="date" className="ta-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="field-date" />
            </div>
            <div>
              <label className="ta-label">Ders</label>
              <select className="ta-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="field-subject">
                <option value="">Seçiniz</option>
                {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="ta-label">Kaynak Adı</label>
              <input className="ta-input" value={form.resource_name} onChange={(e) => setForm({ ...form, resource_name: e.target.value })} data-testid="field-resource_name" />
            </div>
            <div>
              <label className="ta-label">Ünite No</label>
              <input className="ta-input" value={form.unit_no} onChange={(e) => setForm({ ...form, unit_no: e.target.value })} data-testid="field-unit_no" />
            </div>
            <div className="col-span-2">
              <label className="ta-label">Ünite/Konu</label>
              <input className="ta-input" value={form.unit_name} onChange={(e) => setForm({ ...form, unit_name: e.target.value })} data-testid="field-unit_name" />
            </div>
            <div>
              <label className="ta-label">Toplam Soru</label>
              <input type="number" className="ta-input" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} data-testid="field-total" />
            </div>
            <div>
              <label className="ta-label">Doğru</label>
              <input type="number" className="ta-input" value={form.correct} onChange={(e) => setForm({ ...form, correct: e.target.value })} data-testid="field-correct" />
            </div>
            <div>
              <label className="ta-label">Yanlış</label>
              <input type="number" className="ta-input" value={form.wrong} onChange={(e) => setForm({ ...form, wrong: e.target.value })} data-testid="field-wrong" />
            </div>
            <div>
              <label className="ta-label">Boş</label>
              <input type="number" className="ta-input" value={form.blank} onChange={(e) => setForm({ ...form, blank: e.target.value })} data-testid="field-blank" />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="daily-questions-add-btn">
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}<span className="ml-1">Ekle</span>
            </Button>
            <Button type="button" onClick={openResultEntry} className="bg-emerald-600 hover:bg-emerald-700" data-testid="daily-results-btn">
              <ListChecks size={16} /><span className="ml-1">Toplu Sonuç Gir</span>
            </Button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>
      ) : groups.length === 0 ? (
        <div className="card-ta p-10 text-center text-slate-400">Henüz kayıt yok.</div>
      ) : (
        <>
          {groups.map((g, gi) => {
            const rows = items.filter((i) => (i.subject || "Diğer") === g).sort((a, z) => (z.date || "").localeCompare(a.date || ""));
            const tot = rows.reduce((a, r) => a + Number(r.total || 0), 0);
            const cor = rows.reduce((a, r) => a + Number(r.correct || 0), 0);
            const wr = rows.reduce((a, r) => a + Number(r.wrong || 0), 0);
            const bl = rows.reduce((a, r) => a + Number(r.blank || 0), 0);
            return (
              <div key={g} className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm" data-testid={`daily-table-${g}`}>
                <div className={`${HEAD_STYLE[gi % HEAD_STYLE.length]} text-white px-4 py-2.5 flex items-center justify-between`}>
                  <span className="font-display font-bold tracking-wide flex items-center gap-2"><ListChecks size={15} /> {g.toUpperCase()}</span>
                  <span className="text-xs font-semibold opacity-90">{rows.length} kayıt · {tot} soru · Net {(cor - wr / 4).toFixed(2)}</span>
                </div>
                <div className="overflow-x-auto bg-white">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-slate-500 bg-slate-50">
                        <th className="px-4 py-2.5 font-medium">Gün</th>
                        <th className="px-4 py-2.5 font-medium">Tarih</th>
                        <th className="px-4 py-2.5 font-medium">Kaynak</th>
                        <th className="px-4 py-2.5 font-medium">Ünite/Konu</th>
                        <th className="px-4 py-2.5 font-medium text-right">Toplam Soru</th>
                        <th className="px-4 py-2.5 font-medium text-right">Doğru</th>
                        <th className="px-4 py-2.5 font-medium text-right">Yanlış</th>
                        <th className="px-4 py-2.5 font-medium text-right">Boş</th>
                        <th className="px-4 py-2.5 font-medium text-right">Net</th>
                        {!readOnly && <th className="px-4 py-2.5" />}
                      </tr>
                    </thead>
                    <tbody data-testid={`daily-rows-${g}`}>
                      {rows.map((r) => (
                        <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-2.5 font-medium text-slate-700">{dayOf(r.date)}</td>
                          <td className="px-4 py-2.5 text-slate-600">{r.date || "-"}</td>
                          <td className="px-4 py-2.5">
                            {r.source === "kaynak" ? (
                              <span className="text-xs px-2 py-0.5 rounded-full border bg-indigo-50 text-indigo-700 border-indigo-200">{r.resource_name || "Kaynak"}</span>
                            ) : (
                              <span className="text-xs px-2 py-0.5 rounded-full border bg-slate-100 text-slate-600 border-slate-200">{r.resource_name || "Elle"}</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-slate-600">{r.unit_name || "-"}</td>
                          <td className="px-4 py-2.5 text-right font-semibold text-slate-800">{r.total}</td>
                          <td className="px-4 py-2.5 text-right text-emerald-700 font-semibold">{r.correct}</td>
                          <td className="px-4 py-2.5 text-right text-rose-700 font-semibold">{r.wrong}</td>
                          <td className="px-4 py-2.5 text-right text-slate-500">{r.blank ?? 0}</td>
                          <td className="px-4 py-2.5 text-right">{netBadge(r.net)}</td>
                          {!readOnly && (
                            <td className="px-4 py-2.5 text-right">
                              <button onClick={() => remove(r.id)} className="text-slate-300 hover:text-rose-600" data-testid={`delete-${r.id}`}><Trash2 size={14} /></button>
                            </td>
                          )}
                        </tr>
                      ))}
                      <tr className="bg-slate-50 font-semibold" data-testid={`daily-totals-${g}`}>
                        <td className="px-4 py-2.5" colSpan={4}>{g} Toplamı</td>
                        <td className="px-4 py-2.5 text-right">{tot}</td>
                        <td className="px-4 py-2.5 text-right text-emerald-700">{cor}</td>
                        <td className="px-4 py-2.5 text-right text-rose-700">{wr}</td>
                        <td className="px-4 py-2.5 text-right text-slate-500">{bl}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-indigo-700">{(cor - wr / 4).toFixed(2)}</td>
                        {!readOnly && <td />}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5" data-testid="daily-grand-total">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Tüm Dersler Genel Toplam</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                { label: "Toplam Soru", value: grand.t, cls: "text-slate-900" },
                { label: "Doğru", value: grand.c, cls: "text-emerald-600" },
                { label: "Yanlış", value: grand.w, cls: "text-rose-600" },
                { label: "Boş", value: grand.b, cls: "text-slate-500" },
                { label: "Net", value: (grand.c - grand.w / 4).toFixed(2), cls: "text-indigo-600" },
              ].map((c) => (
                <div key={c.label} className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3 text-center">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{c.label}</div>
                  <div className={`font-display text-2xl font-extrabold mt-1 ${c.cls}`}>{c.value}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {resOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setResOpen(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="daily-results-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-bold flex items-center gap-2"><ListChecks size={18} className="text-emerald-600" /> Toplu Sonuç Gir (elle)</h3>
              <button onClick={() => setResOpen(null)} className="text-slate-400 hover:text-slate-700" data-testid="daily-results-close"><X size={18} /></button>
            </div>
            <p className="text-xs text-slate-500 mb-3">Sonuç bekleyen kayıtlar listelenir. Toplam boş bırakılırsa D+Y+B toplamı alınır; net otomatik hesaplanır ve tüm panellerde görünür.</p>
            <div className="space-y-2">
              <div className="grid grid-cols-[1fr_64px_64px_64px_64px] gap-2 text-[11px] font-bold text-slate-400 uppercase px-1">
                <span>Kayıt</span><span className="text-center">Toplam</span><span className="text-center">D</span><span className="text-center">Y</span><span className="text-center">Boş</span>
              </div>
              {resOpen.map((r) => (
                <div key={r.id} className="grid grid-cols-[1fr_64px_64px_64px_64px] gap-2 items-center bg-slate-50 rounded-lg px-2 py-1.5">
                  <div className="text-xs font-semibold text-slate-700 truncate">
                    {r.subject ? <span className="text-indigo-600">{r.subject} · </span> : null}{r.resource_name || r.unit_name || "Kayıt"}
                    <span className="text-slate-400 font-normal"> · {r.date || "-"}</span>
                  </div>
                  <input type="number" className="ta-input !py-1 !text-xs text-center" value={r.t} onChange={(e) => setResOpen(resOpen.map((x) => (x.id === r.id ? { ...x, t: e.target.value } : x)))} data-testid={`res-t-${r.id}`} />
                  <input type="number" className="ta-input !py-1 !text-xs text-center border-emerald-200" value={r.dc} onChange={(e) => setResOpen(resOpen.map((x) => (x.id === r.id ? { ...x, dc: e.target.value } : x)))} data-testid={`res-d-${r.id}`} />
                  <input type="number" className="ta-input !py-1 !text-xs text-center border-rose-200" value={r.yc} onChange={(e) => setResOpen(resOpen.map((x) => (x.id === r.id ? { ...x, yc: e.target.value } : x)))} data-testid={`res-y-${r.id}`} />
                  <input type="number" className="ta-input !py-1 !text-xs text-center" value={r.bl} onChange={(e) => setResOpen(resOpen.map((x) => (x.id === r.id ? { ...x, bl: e.target.value } : x)))} data-testid={`res-b-${r.id}`} />
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setResOpen(null)}>Vazgeç</Button>
              <Button onClick={saveResults} disabled={resSaving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="daily-results-save">
                {resSaving ? <Loader2 className="animate-spin" size={15} /> : <ListChecks size={15} />}<span className="ml-1">Sonuçları Kaydet</span>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
